import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft, Plus, Search, Filter, Edit, Trash2, MapPin, Eye, SortDesc } from 'lucide-react';
import { collection, query, where, getDocs, doc, getDoc, deleteDoc } from 'firebase/firestore';
import { db } from '@config/firebase';
import { useSurvey } from '@contexts/SurveyContext';
import { POI_TYPES } from '../../constants/poiTypes';
import { Button } from '@common/Button';
import { Input, Select } from '@common/FormFields';
import { LoadingSpinner } from '@common/LoadingSpinner';
import { Toast } from '@common/Toast';
import { logError, logInfo } from '@utils/logger';
import { POIForm } from './POIForm';

export const POIListPage = () => {
  const navigate = useNavigate();
  const { activeSurvey } = useSurvey();
  const [pois, setPois] = useState([]);
  const [selectedPoi, setSelectedPoi] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showPoiDetails, setShowPoiDetails] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('createdAt');
  const [sortOrder, setSortOrder] = useState('desc');
  const [filterType, setFilterType] = useState('');

  useEffect(() => {
    const loadPOIs = async () => {
      if (!activeSurvey) return;

      try {
        setLoading(true);
        const poisRef = collection(db, 'pois');
        const q = query(poisRef, where('surveyId', '==', activeSurvey.id));
        const snapshot = await getDocs(q);
        
        const loadedPOIs = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        
        setPois(loadedPOIs);
        logInfo(`Loaded ${loadedPOIs.length} POIs`);
      } catch (error) {
        logError('Failed to load POIs:', error);
        setError('Failed to load POIs');
      } finally {
        setLoading(false);
      }
    };

    loadPOIs();
  }, [activeSurvey]);

  const handleEditPoi = (poi) => {
    setSelectedPoi(poi);
    setShowEditModal(true);
  };

  const handleViewOnMap = (poi) => {
    navigate('/map', {
      state: {
        center: {
          lat: Number(poi.coordinates.latitude),
          lng: Number(poi.coordinates.longitude)
        },
        poiId: poi.id
      }
    });
  };

  // Filter and sort POIs
  const filteredPOIs = pois
    .filter(poi => {
      const matchesSearch = searchTerm === '' || 
        poi.notes?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        poi.id.toLowerCase().includes(searchTerm.toLowerCase());
      
      const matchesType = filterType === '' || poi.type === filterType;
      
      return matchesSearch && matchesType;
    })
    .sort((a, b) => {
      const order = sortOrder === 'asc' ? 1 : -1;
      if (sortBy === 'createdAt') {
        return order * (new Date(a.createdAt) - new Date(b.createdAt));
      }
      return order * (a[sortBy] < b[sortBy] ? -1 : 1);
    });

  return (
    <div className="min-h-screen bg-[#3D3F42]">
      {/* Header */}
      <div className="sticky top-0 bg-[#2A2B2E] shadow-sm z-10 p-4">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate(-1)}
            className="p-2 hover:bg-gray-700 rounded-lg"
          >
            <ArrowLeft className="w-6 h-6 text-white" />
          </button>
          <div>
            <h1 className="text-xl font-semibold text-white">Points of Interest</h1>
            <p className="text-sm text-gray-400">
              {activeSurvey?.name || 'No survey selected'}
            </p>
          </div>
        </div>

        {/* Search and Filters */}
        <div className="mt-4 space-y-4">
          <div className="flex gap-4">
            <div className="flex-1">
              <Input
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search POIs..."
                icon={Search}
                className="bg-gray-700/30 text-white"
              />
            </div>
            <Select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="w-48 bg-gray-700/30 text-white"
            >
              <option value="">All Types</option>
              {Object.keys(POI_TYPES).map(type => (
                <option key={type} value={type}>{type}</option>
              ))}
            </Select>
            <Select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="w-48 bg-gray-700/30 text-white"
            >
              <option value="createdAt">Date Created</option>
              <option value="type">Type</option>
              <option value="id">ID</option>
            </Select>
            <Button
              onClick={() => setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc')}
              className="bg-gray-700 hover:bg-gray-600 text-white"
            >
              <SortDesc className={`w-4 h-4 ${sortOrder === 'desc' ? 'rotate-180' : ''}`} />
            </Button>
          </div>
        </div>
      </div>

      {/* POI List */}
      <div className="p-4">
        {loading ? (
          <div className="flex justify-center py-8">
            <LoadingSpinner />
          </div>
        ) : error ? (
          <div className="text-center py-8 text-red-400">
            {error}
          </div>
        ) : filteredPOIs.length === 0 ? (
          <div className="text-center py-8 text-gray-400">
            No POIs found
          </div>
        ) : (
          <div className="grid gap-4">
            {filteredPOIs.map(poi => {
              const poiType = POI_TYPES[poi.type];
              const Icon = poiType?.icon;
              
              return (
                <div
                  key={poi.id}
                  className="bg-[#2A2B2E] rounded-lg p-4 hover:bg-[#353639] transition-colors"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-3">
                      {Icon && (
                        <div className="p-2 rounded-lg" style={{ backgroundColor: `${poiType.color}20` }}>
                          <Icon className="w-5 h-5" style={{ color: poiType.color }} />
                        </div>
                      )}
                      <div>
                        <div className="flex items-center gap-2">
                          <h3 className="font-medium text-white">
                            {poiType?.prefix || 'POI'}-{poi.id}
                          </h3>
                          <span className="text-xs bg-gray-700 px-2 py-0.5 rounded-full text-gray-300">
                            {poi.type}
                          </span>
                        </div>
                        {poi.notes && (
                          <p className="text-sm text-gray-400 mt-1">{poi.notes}</p>
                        )}
                        {Array.isArray(poi.measurements) && poi.measurements.length > 0 && (
                          <div className="text-sm text-gray-500 mt-1">
                            Measurements: {poi.measurements.map(m => 
                              `${m.value || 0}${m.unit || ''}`
                            ).join(' × ')}
                          </div>
                        )}
                        <div className="text-xs text-gray-500 mt-1">
                          Created {new Date(poi.createdAt).toLocaleString()}
                        </div>
                      </div>
                    </div>

                    <div className="flex gap-2">
                      <Button
                        title="Edit POI"
                        onClick={() => handleEditPoi(poi)}
                        className="p-2 bg-yellow-500/20 text-yellow-400 hover:bg-yellow-500/30 rounded"
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button
                        onClick={() => handleViewOnMap(poi)}
                        className="bg-blue-500/20 hover:bg-blue-500/30 text-blue-400"
                      >
                        View on Map
                      </Button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Edit POI Modal */}
      {showEditModal && selectedPoi && (
        <POIForm
          poi={selectedPoi}
          isEdit={true}
          onClose={() => {
            setShowEditModal(false);
            setSelectedPoi(null);
          }}
          onSave={() => {
            setShowEditModal(false);
            setSelectedPoi(null);
            loadPOIs();
            setToast({
              type: 'success',
              message: 'POI updated successfully'
            });
          }}
        />
      )}

      {/* Toast Notifications */}
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  );
};